import { uploadCategory } from "@/lib/uploadCategory";
import { api } from "./api";

const PARALLEL = 3;
const RETRIES = 4;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

async function putChunk(base, uploadId, index, blob, onChunkProgress, signal) {
  let lastErr;
  for (let attempt = 0; attempt < RETRIES; attempt += 1) {
    if (signal?.aborted) throw new DOMException("Annulé", "AbortError");
    try {
      await api.put(`${base}/uploads/${uploadId}/chunks/${index}`, blob, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => onChunkProgress(e.loaded || 0),
        signal,
      });
      return;
    } catch (err) {
      if (signal?.aborted || err?.code === "ERR_CANCELED") throw new DOMException("Annulé", "AbortError");
      const status = err?.response?.status;
      if (status && status < 500 && status !== 408) throw err;
      lastErr = err;
      onChunkProgress(0);
      await wait(1000 * 2 ** attempt);
    }
  }
  throw lastErr;
}

// Sends every missing chunk (resumable), then commits the version.
export async function sendChunks({ base = "/admin", uploadId, file, chunkSize, totalChunks, received = [], onProgress, signal }) {
  const done = new Set(received);
  const inflight = {};
  let doneBytes = 0;
  done.forEach((i) => {
    doneBytes += Math.min(chunkSize, file.size - i * chunkSize);
  });
  const report = () => {
    const live = Object.values(inflight).reduce((a, b) => a + b, 0);
    onProgress?.(Math.min(100, ((doneBytes + live) / file.size) * 100));
  };
  report();
  const queue = [...Array(totalChunks).keys()].filter((i) => !done.has(i));
  const worker = async () => {
    while (queue.length) {
      const i = queue.shift();
      const blob = file.slice(i * chunkSize, Math.min(file.size, (i + 1) * chunkSize));
      await putChunk(base, uploadId, i, blob, (loaded) => {
        inflight[i] = Math.min(loaded, blob.size);
        report();
      }, signal);
      delete inflight[i];
      doneBytes += blob.size;
      report();
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length || 1) }, worker));
  const { data } = await api.post(`${base}/uploads/${uploadId}/complete`);
  return data;
}

export async function startUpload({ base = "/admin", appId, file, version }) {
  const { data } = await api.post(appId ? `${base}/apps/${appId}/uploads` : `${base}/uploads`, {
    filename: file.name,
    size: file.size,
    version,
    category: appId ? undefined : uploadCategory(base),
  });
  return data;
}

export async function uploadStatus(uploadId, base = "/admin") {
  const { data } = await api.get(`${base}/uploads/${uploadId}`);
  return data;
}

export async function abortUpload(uploadId, base = "/admin") {
  try {
    await api.delete(`${base}/uploads/${uploadId}`);
  } catch {
    /* already gone */
  }
}
